import React, { useEffect, useState } from 'react';
import { CheckCircle, Loader2, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Layout/Header';
import CreateDesignModal from '../components/Layout/CreateDesignModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const USER_KEY = 'ivisora_user';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('loading'); // loading | success | error
  const [plan, setPlan] = useState('pro');
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    const refreshUserPlan = async () => {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token || !API_URL) {
        setStatus('success');
        return;
      }

      // Poll /api/auth/me up to 5 times (webhook may take a second)
      for (let attempt = 0; attempt < 5; attempt++) {
        try {
          const res = await fetch(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            const updatedPlan = data.plan || 'free';
            setPlan(updatedPlan);

            // Update stored user object
            const stored = JSON.parse(localStorage.getItem(USER_KEY) || '{}');
            localStorage.setItem(USER_KEY, JSON.stringify({ ...stored, plan: updatedPlan }));

            if (updatedPlan !== 'free') {
              setStatus('success');
              return;
            }
          }
        } catch (_) {}

        // Wait 1.5 s before next poll
        if (attempt < 4) {
          await new Promise(r => setTimeout(r, 1500));
        }
      }

      // Webhook may still be in-flight — show success anyway
      setStatus('success');
    };

    refreshUserPlan();
  }, [sessionId]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-violet-500" />
          <p className="text-gray-500 font-medium">Activating your plan…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center">
          {/* Animated checkmark */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce-once">
            <CheckCircle className="w-11 h-11 text-green-500" />
          </div>

          <div className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1.5 rounded-full mb-4">
            <Sparkles className="w-3 h-3" />
            {plan === 'enterprise' ? 'Enterprise' : 'Pro'} Plan Active
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            Welcome to {plan === 'enterprise' ? 'Enterprise' : 'Pro'}!
          </h1>
          <p className="text-gray-500 mb-8">
            Your subscription is now active. All premium templates, elements,
            and export formats are unlocked.
          </p>

          <div className="space-y-3">
            <button
              onClick={() => setCreateOpen(true)}
              className="w-full h-12 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-md shadow-violet-200"
            >
              Start Creating <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/templates')}
              className="w-full h-12 border border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Browse Premium Templates
            </button>
          </div>
        </div>
      </div>

      <CreateDesignModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={({ canvasSize, designType }) => {
          setCreateOpen(false);
          navigate('/editor', { state: { canvasSize, designType } });
        }}
      />
    </div>
  );
};

export default PaymentSuccess;
